// Entity-Disables Store: domain-uebergreifender Cache von "diese
// Entitaet ist stillgelegt" -- jede Page (Sensoren/Aktoren/Hosts/...)
// liest hier raus, ob ein Geraet faded gerendert + aus den Aggregaten
// rausgehalten werden soll.
//
// Eine Tabelle hinten dran (entity_disables), eine API-Schnittstelle
// (/v2-api/entity-disables), ein Store hier vorne. Pages adden ihre
// (domain, entity_id)-Konventionen drauf.

import { SvelteMap } from "svelte/reactivity";

import { deleteJSON, getJSON, postJSON } from "./api";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface EntityDisable {
  domain: string;
  entity_id: string;
  disabled_at: string;
  reason: string;
  // expires_at: leer = unbegrenzt, sonst ISO-Zeit. Nach Ablauf gilt der
  // Eintrag client-seitig als nicht mehr aktiv (Auto-Rearm); der Server
  // raeumt ihn separat weg.
  expires_at: string;
}

let _entries = $state<EntityDisable[]>([]);
// key -> expires_at ("" = unbegrenzt). Map statt Set, damit der Hot-Path
// die Ablaufzeit direkt zur Hand hat (Auto-Rearm zwischen Refreshes).
const _expiry = new SvelteMap<string, string>();
let _loaded = $state(false);
let _inflight: Promise<void> | null = null;

function key(domain: string, entityID: string): string {
  return `${domain}::${entityID}`;
}

// expiresActive: true solange das Disable gilt. Leer = unbegrenzt.
function expiresActive(expiresAt: string): boolean {
  if (!expiresAt) return true;
  const t = Date.parse(expiresAt);
  return Number.isNaN(t) || t > Date.now();
}

function refresh(): Promise<void> {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const j = await getJSON<{ disables?: EntityDisable[] }>(
        "/v2-api/entity-disables",
      );
      const list = j.disables ?? [];
      _entries = list;
      _expiry.clear();
      for (const e of list) _expiry.set(key(e.domain, e.entity_id), e.expires_at ?? "");
      _loaded = true;
    } catch {
      // ignore -- naechster Caller versucht's wieder
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

// startEntityDisables wird einmal pro App-Mount aufgerufen (App.svelte).
// Idempotent: doppeltes Anmelden der Hooks ist harmlos (Set / stabile
// Funktions-Identitaet), aber wir gaten trotzdem.
let _started = false;
export function startEntityDisables(): void {
  void refresh();
  if (_started) return;
  _started = true;
  // Push statt Pull: das Backend broadcastet jede Disable-Aenderung auf den
  // "entity-disables"-Channel -- so sieht ein zweiter Tab / das Handy ein in
  // Tab A stillgelegtes Cluster sofort, statt erst beim naechsten Boot.
  wsSubscribe("entity-disables", () => void refresh());
  // Tab-Refocus / Netz-zurueck: holt einen evtl. zwischenzeitlich (z.B. per
  // Auto-Ablauf serverseitig) geaenderten Stand nach und raeumt ein
  // abgelaufenes Badge weg, das rein zeitbasiert sonst kleben bliebe.
  registerRevalidator(() => void refresh());
}

// isEntityDisabled ist die Hot-Path-Frage in jeder Liste -- O(1) ueber den
// Map-Cache. Abgelaufene Eintraege gelten als nicht mehr deaktiviert
// (Auto-Rearm), auch wenn der naechste Refresh sie noch nicht entfernt hat.
export function isEntityDisabled(domain: string, entityID: string): boolean {
  const exp = _expiry.get(key(domain, entityID));
  if (exp === undefined) return false;
  return expiresActive(exp);
}

// entityDisableReason gibt den freitexten Grund zurueck, oder "" wenn
// nicht deaktiviert.
export function entityDisableReason(domain: string, entityID: string): string {
  return _entries.find((e) => e.domain === domain && e.entity_id === entityID)
    ?.reason ?? "";
}

// entityDisableExpiry gibt das Ablaufdatum (ISO) zurueck, oder "" wenn
// unbegrenzt bzw. nicht deaktiviert. Fuer das "deaktiviert bis ..."-Label.
export function entityDisableExpiry(domain: string, entityID: string): string {
  if (!isEntityDisabled(domain, entityID)) return "";
  return _entries.find((e) => e.domain === domain && e.entity_id === entityID)
    ?.expires_at ?? "";
}

// listEntityDisables(domain?) liefert die AKTIV deaktivierten einer Domain
// fuer die "Deaktiviert (3)"-Sektion am Page-Ende. Abgelaufene Eintraege
// werden rausgefiltert (Auto-Rearm). Ohne Argument kommt die volle Liste
// zurueck (Pages mit mehreren Sub-Domains filtern dann selber).
export function listEntityDisables(domain?: string): EntityDisable[] {
  const active = _entries.filter((e) => expiresActive(e.expires_at ?? ""));
  if (domain == null) return active;
  return active.filter((e) => e.domain === domain);
}

export function entityDisablesLoaded(): boolean {
  return _loaded;
}

// disableEntity / enableEntity sind die Write-Aktionen. Beide refreshen
// danach den Cache, damit die UI sofort den neuen Stand zeigt.
/** Legt eine Entität still.
 *
 *  `untilSeen` ist der Normalfall für abgeklemmte GERÄTE: die
 *  Stilllegung endet von selbst, sobald sich das Gerät wieder meldet
 *  (Tim 22.08.: "es wird solange nicht berücksichtigt, bis es sich
 *  wieder meldet oder aktiviert wird"). Man muss dann nicht raten, wie
 *  lange es weg bleibt.
 *
 *  Für dauerhafte Stilllegungen (Pool einer sterbenden Platte, Cluster
 *  in Wartung) bleibt es aus: dort soll ein kurzes Lebenszeichen die
 *  Entscheidung gerade NICHT umwerfen. */
export async function disableEntity(
  domain: string,
  entityID: string,
  reason: string,
  expiresAt = "",
  untilSeen = false,
): Promise<void> {
  await postJSON("/v2-api/entity-disables", {
    domain,
    entity_id: entityID,
    reason,
    expires_at: expiresAt,
    until_seen: untilSeen,
  });
  await refresh();
}

export async function enableEntity(
  domain: string,
  entityID: string,
): Promise<void> {
  await deleteJSON(
    `/v2-api/entity-disables/${encodeURIComponent(domain)}/${encodeURIComponent(entityID)}`,
  );
  await refresh();
}
